export interface ImageEditorConfig {
  [string: string]: ImageConfig;
}

export interface ImageConfig {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  layerIndex: number;
  refId: string;
  id?: string;
  ClientKey: string;
}

function normalizeSavedImageConfig(raw: any): ImageConfig | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const clientKey = raw.ClientKey || raw.refId || '';
  if (!clientKey && raw.x === undefined && raw.layerIndex === undefined) {
    return null;
  }

  return {
    x: raw.x ?? 180,
    y: raw.y ?? 180,
    refId: raw.refId || clientKey,
    scaleX: raw.scaleX ?? 0.5,
    scaleY: raw.scaleY ?? 0.5,
    rotation: raw.rotation ?? 0,
    layerIndex: raw.layerIndex ?? 0,
    id: raw.id ?? raw.Id ?? '',
    ClientKey: clientKey || raw.refId,
  };
}

/** Reads saved layout from Detail.Config and ChildAssets (legacy + new editor formats). */
export function parseSavedMontageConfigs(config: any): ImageEditorConfig {
  const saved: ImageEditorConfig = {};

  const addConfig = (key: string, raw: any) => {
    const normalized = normalizeSavedImageConfig(raw);
    if (!normalized) {
      return;
    }

    const configKey = normalized.ClientKey || normalized.refId || key;
    saved[configKey] = normalized;
  };

  if (config?.Detail?.Config) {
    try {
      const detailConfig = JSON.parse(config.Detail.Config);
      if (detailConfig && typeof detailConfig === 'object' && !Array.isArray(detailConfig)) {
        Object.entries(detailConfig).forEach(([key, value]) => addConfig(key, value));
      }
    } catch {
      // ignore invalid json
    }
  }

  (config?.ChildAssets ?? []).forEach((childAsset: any) => {
    try {
      const parsed = JSON.parse(childAsset.Config);
      if (parsed?.montageImageConfig) {
        addConfig(
          parsed.montageImageConfig.refId || parsed.montageImageDetails?.wicId || '',
          parsed.montageImageConfig
        );
      } else {
        addConfig(parsed.ClientKey || parsed.refId || '', parsed);
      }
    } catch {
      // ignore invalid json
    }
  });

  return saved;
}

export function getSavedImageConfig(
  savedConfigs: ImageEditorConfig,
  clientKey: string
): ImageConfig | undefined {
  if (!clientKey) {
    return undefined;
  }

  if (savedConfigs[clientKey]) {
    return savedConfigs[clientKey];
  }

  return Object.values(savedConfigs).find(
    (config) => config.ClientKey === clientKey || config.refId === clientKey
  );
}

export function getSelectionAssetId(item: any): string {
  if (item?.FacingAsset?.AssetId) {
    return item.FacingAsset.AssetId;
  }

  return item?.DomainId ?? item?.Id ?? '';
}

export function getUrlParams(selections: any[]): string {
  const assetIds = selections.map(getSelectionAssetId).filter(Boolean);

  return assetIds.map((assetId) => `assetIds=${assetId}`).join('&');
}

export function getAssetGroupName(isOfferGroup: boolean): string {
  return isOfferGroup ? 'Offer Groups' : 'Offers';
}

/** Show history when an existing montage belongs to a different offer / offer group. */
export function shouldShowMontageHistory(
  historyItem: any,
  isOfferGroup: boolean,
  offerId: string,
  imageEditorConfigId?: string
): boolean {
  if (!historyItem || imageEditorConfigId) {
    return false;
  }

  if (isOfferGroup) {
    return !!historyItem.OfferGroupId && offerId !== historyItem.OfferGroupId;
  }

  return !!historyItem.OfferId && offerId !== historyItem.OfferId;
}

/** Pre-edit gate: skip when history is this offer's montage (AssetId match); show cross-offer history only. */
export function shouldShowPreEditMontageHistory(
  historyItem: any,
  offerId: string,
  isOfferGroup: boolean,
  currentMontageAssetId?: string
): boolean {
  if (!historyItem?.AssetId) {
    return false;
  }

  if (currentMontageAssetId && historyItem.AssetId === currentMontageAssetId) {
    return false;
  }

  if (isOfferGroup) {
    return !!historyItem.OfferGroupId && offerId !== historyItem.OfferGroupId;
  }

  return !!historyItem.OfferId && offerId !== historyItem.OfferId;
}

export function buildJaiSavedConfigs(settings: Record<string, any>): ImageEditorConfig {
  const saved: ImageEditorConfig = {};

  Object.entries(settings ?? {}).forEach(([clientKey, layout]) => {
    if (!layout || typeof layout !== 'object') {
      return;
    }

    saved[clientKey] = {
      x: layout.x ?? 180,
      y: layout.y ?? 180,
      refId: layout.refId ?? clientKey,
      scaleX: layout.scaleX ?? 0.5,
      scaleY: layout.scaleY ?? 0.5,
      rotation: layout.rotation ?? 0,
      layerIndex: layout.layerIndex ?? 0,
      ClientKey: clientKey,
    };
  });

  return saved;
}